import React, { Component, MouseEvent } from 'react';
import Tooltip from 'rc-tooltip';
import cn from 'classnames';
import path from 'path-browserify';
import { HotKeys, Handlers } from 'react-keyboard';

import { Renamer } from '../../renamer';
import { FileIcon } from '../file-icon';
import { GenConfirmationModal } from '../../../../atoms/confirmation-modal/genesis';
import { Indent } from '../indent';
import { TreeNodeProps, TreeModel, TreeModelContext } from '../../../../atoms/tree-layer';

import styles from './file-view.module.scss';
import { ActionBox, Action } from '../action-box';
import { Icon } from '../../../../atoms/icon';
import { Filename } from '../filename';
import { DraggableTreeNode } from '../../../../atoms/dragDrop';

export class FileView extends Component<TreeNodeProps<undefined>> {
	state = { isRenaming: false, showDeleteConfirmation: false };

	static contextType = TreeModelContext;
	context!: TreeModel;

	handleSelect = () => {
		const { node } = this.props;
		const treeModel = this.context;

		//TODO! file may not be selectable
		treeModel.selectFile(node.id);
	};

	handleDelete = (event?: { stopPropagation: () => any }) => {
		event && event.stopPropagation();
		this.setState({ showDeleteConfirmation: true });
	};

	handleStartRename = () => {
		this.setState({ isRenaming: true });
	};

	handleRenameFile = (newBasename: string) => {
		const treeModel = this.context;
		const { node } = this.props;
		const { id } = node;

		this.setState({ isRenaming: false });
		this.refocus();

		if (!newBasename) return;

		const basename = path.basename(id);
		const dirname = path.dirname(id);
		if (newBasename === basename) return;

		const newPath: string = path.join(dirname, newBasename);

		return treeModel.rename(id, newPath);
	};

	handleAbortRename = () => {
		this.setState({ isRenaming: false });

		this.refocus();
	};

	handleValidateRename = (nextName: string, prevName?: string) => {
		if (nextName === prevName) return true;

		const { node } = this.props;
		const { id } = node;

		const dirname = path.dirname(id);

		const treeModel = this.context;
		return (
			treeModel.isValidFilename(nextName) &&
			treeModel.canRename(path.join(dirname, nextName), path.join(dirname, prevName))
		);
	};

	handleCancelDelete = () => {
		this.setState({ showDeleteConfirmation: false });
	};

	handleConfirmDelete = () => {
		this.setState({ showDeleteConfirmation: false });
		const { node } = this.props;
		const { id } = node;

		const treeModel = this.context;
		treeModel.unlink(id);
	};

	refocus = () => {
		const hotkeysRef = this.hotkeysRef.current;
		if (hotkeysRef) {
			hotkeysRef.focus();
		}
	};

	keyHandlers: Handlers = {
		rename: (e?: KeyboardEvent) => this.setState({ isRenaming: true }),
		delete: (e?: KeyboardEvent) => {
			if (this.state.isRenaming) return;
			this.handleDelete();
		},
	};

	hotkeysRef = React.createRef<HotKeys>();

	render() {
		const { isRenaming } = this.state;
		const { node, depth } = this.props;
		const { id } = node;
		const basename = path.basename(id);

		const treeModel = this.context;
		const isSelected = treeModel.selectedFile === id;

		return (
			<HotKeys
				focusOnMount={false}
				className={styles.keyBindings}
				ref={this.hotkeysRef}
				handlers={this.keyHandlers}
			>
				<DraggableTreeNode node={node}>
					<Indent
						className={cn(styles.file, { [styles.activeFile]: isSelected })}
						depth={depth}
						onClick={this.handleSelect}
					>
						<FileIcon className={styles.filetypeIcon} fileName={basename} />
						{isRenaming && (
							<Renamer
								value={basename}
								validate={this.handleValidateRename}
								onSubmit={this.handleRenameFile}
								onAbort={this.handleAbortRename}
							/>
						)}
						{!isRenaming && <Filename>{basename}</Filename>}

						<ActionBox>
							<Tooltip overlay="rename" placement="bottom" mouseLeaveDelay={0}>
								<Action onClick={this.handleStartRename}>
									<Icon of="pencil" />
								</Action>
							</Tooltip>

							<Tooltip overlay="delete" placement="bottom" mouseLeaveDelay={0}>
								<Action onClick={this.handleDelete}>
									<Icon of="trash" />
								</Action>
							</Tooltip>
						</ActionBox>
					</Indent>
					{/* TODO - consider moving outside of <HotKeys/> */}
				</DraggableTreeNode>
				<GenConfirmationModal
					isOpen={this.state.showDeleteConfirmation}
					title="Are you sure you want to delete this file?"
					confirm="Delete"
					cancel="Cancel"
					onCancel={this.handleCancelDelete}
					onConfirm={this.handleConfirmDelete}
				>
					<div>{id}</div>
				</GenConfirmationModal>
			</HotKeys>
		);
	}
}
